import { usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { BrandMotif } from "@/components/ui/brand-motif"
import type { BaseBlockData } from "@/components/gisela/SanctuaryBaseBlock"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  content: {
    online_heading: string
    online_body: string
    in_person_body: string
    // One short blurb per location card, keyed "<site slug>_body"
    // (prestwich_body, hebden_bridge_body) — admin copy alongside the online card's.
    [key: string]: string | boolean | undefined
  }
  // Her in-person bases. Present only on her own host, where the cross-links
  // to the Sanctuary site resolve.
  base?: BaseBlockData | null
  // Admin-placed photo for the online card ("therapy.online_sessions" slot);
  // the card runs as a colour field until one is placed.
  photo?: PlacementPhoto | null
  /** The band's heading — the home page's by default; the Where We Meet
   *  landing page passes its own, since its hero already says "Where we meet". */
  heading?: string
}

// "Where we meet": one card per way of working with her — online, then each
// of the two Sanctuary Therapy Centre bases she founded. The photos are the
// same ones the Sanctuary site uses for its own location cards, cropped
// square so the rooms keep their detail (a circle would crop it away). The
// intro is the admin's in-person copy; the online card is theirs too.
export default function WorkingTogether({ content, base, photo, heading }: Props) {
  const { props } = usePage<PageProps>()
  const tp = props.translations.gisela_pages.profile
  const tb = props.translations.gisela_pages.base
  const tn = props.translations.gisela_nav
  const lp = useLocalePath()
  const onGiselaHost = props.current_brand.key === "gisela"
  const locations = base?.locations ?? []

  if (!content.online_body && !content.in_person_body) return null

  const cardImage = "aspect-[4/3] w-full object-cover"

  return (
    <section className="relative overflow-hidden bg-stone py-12 lg:py-14">
      <BrandMotif variant="sprig" className="absolute -bottom-10 left-6" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {/* Heading over its intro at the page's left margin — the same opening
            as "Where would you like to start?" and "How I work", rather than
            the heading-in-a-left-gutter split this band used to have. */}
        <div className="max-w-3xl">
          <h2>{heading ?? tp.working_heading}</h2>
          {content.in_person_body && (
            <div className="body-content mt-3 text-ink-muted">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.in_person_body}</ReactMarkdown>
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {content.online_body && (
            <div className="flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-hairline">
              {photo ? (
                <img src={photo.card_url} alt={photo.alt_text ?? ""} className={cardImage} loading="lazy" decoding="async" />
              ) : (
                <div aria-hidden className="aspect-[4/3] w-full bg-sage-light/60" />
              )}
              <div className="flex flex-grow flex-col p-6">
                <h3>{content.online_heading}</h3>
                <div className="body-content mt-3 text-[15px] text-ink-muted">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.online_body}</ReactMarkdown>
                </div>
                {onGiselaHost && (
                  <a href={lp("/online-therapy")} className="mt-auto pt-4 text-sm font-medium text-teal underline underline-offset-4">
                    {tn.online_therapy} →
                  </a>
                )}
              </div>
            </div>
          )}

          {locations.map((location) => (
            <div key={location.path} className="flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-hairline">
              {location.photo_url ? (
                <img src={location.photo_url} alt={location.name} className={cardImage} loading="lazy" decoding="async" />
              ) : (
                <div aria-hidden className="aspect-[4/3] w-full bg-stone-mid" />
              )}
              <div className="flex flex-grow flex-col p-6">
                {/* One heading line per card, level with the online card's:
                    the region already reads "Prestwich, Manchester". */}
                <h3>
                  <a href={lp(location.town_path)} className="no-underline hover:underline">
                    {location.region || location.name}
                  </a>
                </h3>
                {location.address && <p className="mt-2 text-[15px] leading-relaxed text-ink-muted">{location.address}</p>}
                {typeof content[`${location.slug.replace(/-/g, "_")}_body`] === "string" && (
                  <p className="mt-3 text-[15px] leading-relaxed text-ink-muted">
                    {content[`${location.slug.replace(/-/g, "_")}_body`] as string}
                  </p>
                )}
                <span className="mt-auto flex flex-wrap items-center gap-x-2 pt-4 text-sm font-medium">
                  {/* Her page for the town — the rooms, the photos and how to
                      get there are all on it — rather than the venue's gallery. */}
                  <a href={lp(location.town_path)} className="text-teal underline underline-offset-4">
                    {tn.therapy_in_town.replace("%{name}", location.name)}
                  </a>
                  {location.maps_url && (
                    <>
                      <span aria-hidden className="text-ink-muted">·</span>
                      <a href={location.maps_url} target="_blank" rel="noopener" className="text-teal underline underline-offset-4">
                        {tb.map}
                      </a>
                    </>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
